import * as React from "react";
import { Link } from "react-router";
import { ArrowLeft, Check } from "lucide-react";
import { IconList } from "@/components/ui/IconList";
import { PanelGrid } from "@/components/ui/PanelGrid";
import { TaskIqLogo, VisionifyByline } from "./Brand";
import { SecondScale } from "./marketing/SecondScale";
import { cn } from "@/lib/cn";

/**
 * The third shell: sign-in, sign-up, onboarding, password reset.
 *
 * Neither the marketing site's top navigation nor the console's rail is right
 * here. Navigation on a sign-in page is an invitation to leave it, and a rail
 * requires the very session the visitor is trying to establish, so the auth
 * screens get a layout of their own whose only job is one form and one way back.
 *
 * Two columns on desktop: a panel carrying the positioning line and the same
 * ninety-second scale the marketing site is built on, then the form. The scale is
 * not decoration here. This is the screen where someone who arrived from an
 * invitation email sees the product for the first time, with no idea what it is,
 * and the ruler is the fastest honest explanation available.
 *
 * Below 1024px the panel is dropped and the card dissolves into the page: a
 * bordered card floating on a phone screen is a smaller viewport for no benefit.
 *
 * ── This column must not scroll ─────────────────────────────────────────────
 *
 * Every screen using this layout is meant to fit a laptop viewport whole. That
 * is a constraint on the *forms*, not something this file can enforce on its
 * own, and it is the reason sign-up is four short screens rather than one long
 * one: five fields plus three social buttons is roughly 1300px of content and no
 * amount of tightening makes that fit in 800px.
 *
 * What this file contributes is the rhythm. The vertical scale here is
 * deliberately one step tighter than the console's, and the controls inside are
 * `md` (44px) rather than `lg` (56px). 56px is the glove-operable floor from PRD
 * §11 and it is correct on the phone, where a technician is wearing gloves in
 * the field. Nobody signs up for a SaaS console wearing gloves. 44px is still
 * the standard touch target, so nothing is lost but the scrollbar.
 *
 * Keep it that way: if a new screen here needs a scrollbar, split the screen.
 */
/**
 * The way back, as an icon on the title's own line.
 *
 * A `to` navigates; an `onClick` runs a callback without leaving the screen -
 * "change email address" on the sign-up code step is a reset of local state,
 * not a route. Exactly one of the two is expected; `to` wins if both are
 * somehow passed, since a navigation is the harder promise to break silently.
 */
export type AuthBack = { label: string } & ({ to: string } | { onClick: () => void });

export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  /** Shown beside the title, e.g. back to sign-in from the reset form. */
  back,
}: {
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  back?: AuthBack;
}) {
  return (
    <div className="auth">
      {/* ── Brand panel, desktop only ──────────────────────────────────── */}
      <aside className="on-panel auth__panel">
        <PanelGrid origin="top-left" />

        {/* A measure of its own, centred. The panel is `1fr` of the layout, so
            on a wide screen it is 1400px of near-black with the copy pinned to
            the left of it - the content was not too small, it was too far from
            the middle of the space it had. */}
        <div className="auth__panel-inner">
          <Link to="/" aria-label="TaskIQ home" className="auth__panel-brand">
            <TaskIqLogo tone="panel" size="lg" />
          </Link>

          <div className="auth__pitch">
            <p className="auth__pitch-title">The last ninety seconds before the job starts.</p>
            <p className="auth__pitch-body">
              Every other safety tool tells you what went wrong. TaskIQ tells you what is in front
              of you, before you start.
            </p>

            <div className="auth__scale">
              <p className="eyebrow eyebrow--sm eyebrow--panel-subtle">One assessment</p>
              <SecondScale tone="panel" animated className="auth__scale-bar" />
            </div>
          </div>

          {/* The claims and the endorsement are one block so the column stays
              three flex children and `justify-between` keeps meaning what it
              says. */}
          <div className="auth__panel-foot">
            <IconList
              tone="panel-accent"
              className="auth__claims-list"
              items={[
                { icon: Check, text: "Works with no signal" },
                { icon: Check, text: "No cameras to install" },
                { icon: Check, text: "Never facial recognition" },
              ]}
            />
            <Legal tone="panel" />
          </div>
        </div>
      </aside>

      {/* ── Form column ────────────────────────────────────────────────── */}
      <main className="auth__form-col">
        <div className="auth__form-measure">
          <Link to="/" className="auth__mobile-brand">
            <TaskIqLogo size="sm" />
          </Link>

          {/*
           * The way back sits on the title's own line, as an arrow, on every
           * screen in this flow that has one - not just sign-in.
           *
           * It used to be a labelled link on a row above the title on some
           * screens, a four-step progress bar with no way back at all on
           * others, and nothing on the rest: sign-up's first screen had no
           * way out except the browser button, while sign-in right next to
           * it in the nav had one. Consistency is the point of a shared
           * layout, and a control that only some of its screens carry is the
           * layout failing at that job.
           *
           * The stepper is gone with it. It cost 30-40px of the vertical
           * budget this layout exists to protect, on a flow that is already
           * four screens because that budget is tight, and the two things it
           * told a reader - "there is more coming" and "here is roughly how
           * much" - are said just as clearly by a title that changes ("Check
           * your inbox" reads as progress on its own) and by a product whose
           * whole pitch is that nothing here takes long.
           */}
          <div className="auth__title-row">
            {back &&
              ("to" in back ? (
                <Link
                  to={back.to}
                  aria-label={back.label}
                  title={back.label}
                  className="auth__back"
                >
                  <ArrowLeft />
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={back.onClick}
                  aria-label={back.label}
                  title={back.label}
                  className="auth__back"
                >
                  <ArrowLeft />
                </button>
              ))}
            <h1 className="auth__title">{title}</h1>
          </div>
          {subtitle && <p className="auth__subtitle">{subtitle}</p>}

          <div className="auth__body">{children}</div>

          {footer && <div className="auth__footer">{footer}</div>}

          {/* Desktop carries this in the brand panel, where it belongs with the
              endorsement rather than under the submit button. Below `lg` there
              is no panel, so it comes back here. */}
          <Legal tone="accent" className="auth__legal--mobile" />
        </div>
      </main>
    </div>
  );
}

function Legal({ tone, className }: { tone: "accent" | "panel"; className?: string }) {
  return (
    <div className={cn("auth__legal", tone === "panel" && "auth__legal--panel", className)}>
      <VisionifyByline tone={tone} />
      <div className="auth__legal-links">
        <a
          href="https://visionify.ai/privacy"
          target="_blank"
          rel="noreferrer noopener"
          className="auth__legal-link"
        >
          Privacy
        </a>
        <a
          href="https://visionify.ai/terms"
          target="_blank"
          rel="noreferrer noopener"
          className="auth__legal-link"
        >
          Terms
        </a>
      </div>
    </div>
  );
}
